import { useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch } from '../api/client.js';
import { fmtAgo } from '../lib/labels.js';

/**
 * Shared TikTok posting-options UI. Single source of truth for the consent
 * fields TikTok's Content Posting audit requires:
 *   - which creator the post goes out as (nickname + avatar, live creator_info);
 *   - a privacy dropdown built from the creator's OWN privacy_level_options,
 *     with NO preselected default;
 *   - commercial-content disclosures (branded content can never be "Only you");
 *   - Music Usage Confirmation consent before saving.
 *
 * The same consent fields are reused in two places, so they never drift:
 *   - per post, in a modal from the list / inbox / post page (TikTokOptionsButton);
 *   - per connection, as a DEFAULT set once on the TikTok connection
 *     (TikTokDefaultsButton) — the publisher falls back to these when a post
 *     has no per-post options, so an owner configures them once instead of
 *     on every post.
 */
export const TIKTOK_PRIVACY_LABELS = {
  PUBLIC_TO_EVERYONE: 'Everyone',
  MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only you',
};
const TIKTOK_MUSIC_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en';
const TIKTOK_BC_POLICY_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en';

// Single FRONTEND definition of "this post is a video/reel" (not supported
// yet) — used by the button gate, the form, and PostPublications' pending
// chip so they never diverge. MUST MATCH platformSkipReason's reel check in
// workers/src/publishWorker.js (services can't import a shared package —
// Docker build contexts exclude it; same pattern as api/src/lib/platforms.js).
export function isReelPost(post) {
  return post?.payload?.format === 'reel' || post?.pillar_key === 'reel';
}

/**
 * Presentational consent form. Owns the local field state (seeded from
 * `saved`, reset when a fresh `saved` arrives) and renders creator row +
 * privacy dropdown + disclosures + comments + Music Usage Confirmation +
 * save/clear. The caller supplies the data source (creator, saved) and the
 * save/clear handlers, so the exact same fields drive per-post and
 * per-connection saves.
 */
function TikTokConsentFields({
  entityKey, saved, canEdit, creator, creatorLoading, creatorError, onRetryCreator,
  accountHandle, onSave, onClear, saving, msg, onDirty,
}) {
  const fieldId = useId();
  const [privacy, setPrivacy] = useState(saved?.privacy_level || '');
  const [disclose, setDisclose] = useState(Boolean(saved?.brand_organic || saved?.brand_content));
  const [brandOrganic, setBrandOrganic] = useState(Boolean(saved?.brand_organic));
  const [brandContent, setBrandContent] = useState(Boolean(saved?.brand_content));
  const [commentsOff, setCommentsOff] = useState(Boolean(saved?.disable_comment));
  const [musicOk, setMusicOk] = useState(Boolean(saved?.music_confirmed));

  useEffect(() => {
    setPrivacy(saved?.privacy_level || '');
    setDisclose(Boolean(saved?.brand_organic || saved?.brand_content));
    setBrandOrganic(Boolean(saved?.brand_organic));
    setBrandContent(Boolean(saved?.brand_content));
    setCommentsOff(Boolean(saved?.disable_comment));
    setMusicOk(Boolean(saved?.music_confirmed));
    // entityKey (post id / account id): without it, swapping the mounted
    // form to a DIFFERENT entity whose saved options are equally absent
    // (confirmed_at undefined for both) never re-fired this reset, so
    // unsaved selections leaked across entities (review 2026-08-24).
  }, [entityKey, saved?.confirmed_at]);

  const privacyOptions = creator?.privacyLevelOptions || [];
  // A previously-saved level TikTok no longer offers for this creator must
  // force a fresh choice — otherwise the select LOOKS blank while state
  // still holds the stale value and Save silently re-persists it.
  const privacyStale = Boolean(privacy) && privacyOptions.length > 0 && !privacyOptions.includes(privacy);
  const privacyValue = privacyStale ? '' : privacy;
  const privacyConflict = brandContent && privacy === 'SELF_ONLY';
  const discloseIncomplete = disclose && !brandOrganic && !brandContent;
  const canSave = canEdit && privacy && !privacyStale && musicOk && !privacyConflict && !discloseIncomplete && !saving;

  return (
    <>
      {creatorLoading && <div className="muted">Checking the TikTok account…</div>}
      {creatorError && (
        <div className="post-action-msg err">
          Couldn’t reach TikTok for this account’s posting options: {creatorError}
          {' '}<button type="button" className="btn btn-ghost btn-icon" title="Retry" aria-label="Retry"
            onClick={onRetryCreator}>↻</button>
        </div>
      )}

      {creator && (
        <>
          <div className="tiktok-creator-row">
            {creator.avatarUrl && <img className="tiktok-creator-avatar" src={creator.avatarUrl} alt="" />}
            <span>Posts as <b>{creator.nickname || accountHandle}</b></span>
            <span className="target-handle">{accountHandle}</span>
          </div>

          <div className="tiktok-consent-field">
            <label htmlFor={fieldId}>Who can view this post</label>
            <select
              id={fieldId}
              value={privacyValue}
              disabled={!canEdit}
              onChange={(e) => { setPrivacy(e.target.value); onDirty?.(); }}
            >
              <option value="" disabled>Choose who can view…</option>
              {privacyOptions.map((opt) => (
                <option key={opt} value={opt} disabled={brandContent && opt === 'SELF_ONLY'}>
                  {TIKTOK_PRIVACY_LABELS[opt] || opt}
                </option>
              ))}
            </select>
            {privacyStale && (
              <span className="post-action-hint">
                The previously saved visibility isn’t offered for this account anymore — choose again.
              </span>
            )}
            <span className="post-action-hint">
              Until TikTok’s content audit approves the app, TikTok forces posts to “Only you” and the account must stay private.
            </span>
          </div>

          <label className="tiktok-consent-check">
            <input
              type="checkbox"
              checked={disclose}
              disabled={!canEdit}
              onChange={(e) => {
                setDisclose(e.target.checked);
                if (!e.target.checked) { setBrandOrganic(false); setBrandContent(false); }
              }}
            />
            <span>This post promotes a brand, product or service</span>
          </label>

          {disclose && (
            <div className="tiktok-consent-subchecks">
              <label className="tiktok-consent-check">
                <input
                  type="checkbox"
                  checked={brandOrganic}
                  disabled={!canEdit}
                  onChange={(e) => setBrandOrganic(e.target.checked)}
                />
                <span>Your brand — promoting your own business <span className="muted small">(labeled “Promotional content”)</span></span>
              </label>
              <label className="tiktok-consent-check">
                <input
                  type="checkbox"
                  checked={brandContent}
                  disabled={!canEdit}
                  onChange={(e) => setBrandContent(e.target.checked)}
                />
                <span>Branded content — paid partnership with another brand <span className="muted small">(labeled “Paid partnership”)</span></span>
              </label>
              {discloseIncomplete && <div className="muted small">Pick at least one disclosure, or turn the toggle off.</div>}
              {privacyConflict && <div className="post-action-msg err">Branded content can’t be “Only you” — pick a visible privacy level.</div>}
            </div>
          )}

          <label className="tiktok-consent-check">
            <input
              type="checkbox"
              checked={commentsOff}
              disabled={!canEdit}
              onChange={(e) => setCommentsOff(e.target.checked)}
            />
            <span>Turn off comments</span>
          </label>

          <label className="tiktok-consent-check">
            <input
              type="checkbox"
              checked={musicOk}
              disabled={!canEdit}
              onChange={(e) => { setMusicOk(e.target.checked); onDirty?.(); }}
            />
            <span>
              By posting, you agree to TikTok’s{' '}
              {brandContent && (
                <>
                  <a href={TIKTOK_BC_POLICY_URL} target="_blank" rel="noreferrer">Branded Content Policy</a>
                  {' and '}
                </>
              )}
              <a href={TIKTOK_MUSIC_URL} target="_blank" rel="noreferrer">Music Usage Confirmation</a>.
            </span>
          </label>

          <div className="post-action-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canSave}
              onClick={() => onSave({
                privacy_level: privacy,
                brand_organic: brandOrganic,
                brand_content: brandContent,
                disable_comment: commentsOff,
                music_confirmed: musicOk,
              })}
            >
              {saving ? 'Saving…' : 'Save TikTok options'}
            </button>
            {saved && (
              <button
                type="button"
                className="btn"
                disabled={!canEdit || saving}
                onClick={onClear}
              >
                Clear
              </button>
            )}
            {saved && !saving && (
              <span className="muted small">
                Saved {saved.confirmed_at ? fmtAgo(saved.confirmed_at) : ''}{saved.confirmed_by ? ` by ${saved.confirmed_by}` : ''}.
              </span>
            )}
          </div>
          {msg && <div className={`post-action-msg ${msg.kind === 'ok' ? 'ok' : 'err'}`}>{msg.text}</div>}
        </>
      )}
    </>
  );
}

/**
 * Per-post consent form (modal body). Returns null when TikTok can't/won't
 * receive this post (no active account, excluded by per-post targets, or a
 * video post).
 */
export function TikTokOptionsForm({ post, publications, onSaved }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState(null);
  useEffect(() => { setMsg(null); }, [post.id]);

  const accountsQ = useQuery({
    queryKey: ['social-accounts', post.project_key],
    queryFn: ({ signal }) => apiGet(`/social/accounts?project=${encodeURIComponent(post.project_key)}`, { signal }),
    enabled: Boolean(post.project_key),
  });
  const accounts = accountsQ.data?.accounts ?? [];
  // The exact account the /posts SQL gate chose (respects the post's target
  // overrides). FAIL CLOSED when that id is set but no longer active:
  // showing and saving consent against a DIFFERENT creator is worse than
  // showing nothing (review 2026-08-24). First-active fallback only when
  // the caller genuinely didn't supply an id (post-detail endpoint).
  const tiktok = post.tiktok_account_id
    ? (accounts.find((a) => a.id === post.tiktok_account_id && a.status === 'active') || null)
    : (accounts.find((a) => a.platform === 'tiktok' && a.status === 'active') || null);

  const overrides = post.post_target_overrides;
  const targeted = Boolean(tiktok) && (!Array.isArray(overrides) || overrides.includes(tiktok.id));
  const isReel = isReelPost(post);
  // Publications field name differs by source: the post page aliases
  // post_publications.state as `publication_state`; the /posts list uses plain
  // `state`. Accept either.
  const tiktokPub = (publications || post.publications || []).find((p) => p.platform === 'tiktok');
  const alreadyPublished = (tiktokPub?.publication_state || tiktokPub?.state) === 'published';
  const canEdit = ['rendered', 'review', 'approved', 'failed', 'published'].includes(post.state) && !alreadyPublished;

  const saved = post.platform_options?.tiktok || null;

  const creatorQ = useQuery({
    queryKey: ['creator-info', tiktok?.id],
    queryFn: ({ signal }) => apiGet(`/social/accounts/${tiktok.id}/creator-info`, { signal }),
    enabled: Boolean(tiktok) && targeted && !isReel && !alreadyPublished,
    staleTime: 5 * 60 * 1000, // creator_info is rate-limited (20/min per account)
    retry: 1,
  });

  const saveM = useMutation({
    mutationFn: (options) => apiPatch(`/posts/${post.id}/platform-options`, { platform: 'tiktok', options }),
    onSuccess: (res) => {
      setMsg(res.options
        ? { kind: 'ok', text: 'Saved — TikTok will publish with these choices.' }
        : { kind: 'ok', text: 'Cleared — TikTok falls back to the connection default / “Only you”.' });
      queryClient.invalidateQueries({ queryKey: ['post', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      onSaved?.(res);
    },
    onError: (err) => setMsg({ kind: 'err', text: err.body?.detail || err.message }),
  });

  if (!tiktok || !targeted || isReel) return null;

  let body;
  if (alreadyPublished) {
    body = (
      <div className="muted small">
        Published to TikTok as <b>{TIKTOK_PRIVACY_LABELS[saved?.privacy_level] || 'Only you'}</b>
        {saved?.brand_content ? ' · labeled Paid partnership' : saved?.brand_organic ? ' · labeled Promotional content' : ''}.
      </div>
    );
  } else {
    body = (
      <TikTokConsentFields
        entityKey={post.id}
        saved={saved}
        canEdit={canEdit}
        creator={creatorQ.data?.creator || null}
        creatorLoading={creatorQ.isLoading}
        creatorError={creatorQ.isError ? (creatorQ.error.body?.detail || creatorQ.error.message) : null}
        onRetryCreator={() => creatorQ.refetch()}
        accountHandle={tiktok.external_account_handle}
        onSave={(options) => saveM.mutate(options)}
        onClear={() => saveM.mutate(null)}
        saving={saveM.isPending}
        msg={msg}
        onDirty={() => setMsg(null)}
      />
    );
  }

  return body;
}

/**
 * Per-connection DEFAULT options form. Same consent fields, but saves to the
 * TikTok connection (PATCH /social/accounts/:id/default-options). Used inside
 * the Connections gear modal.
 */
export function TikTokDefaultsForm({ account, projectKey, onSaved }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState(null);
  useEffect(() => { setMsg(null); }, [account.id]);

  const saved = account.default_post_options?.tiktok || null;

  const creatorQ = useQuery({
    queryKey: ['creator-info', account.id],
    queryFn: ({ signal }) => apiGet(`/social/accounts/${account.id}/creator-info`, { signal }),
    enabled: account.status === 'active',
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const saveM = useMutation({
    mutationFn: (options) => apiPatch(`/social/accounts/${account.id}/default-options`, { platform: 'tiktok', options }),
    onSuccess: (res) => {
      setMsg(res.options
        ? { kind: 'ok', text: 'Saved — new TikTok posts use these unless a post sets its own.' }
        : { kind: 'ok', text: 'Cleared — posts fall back to per-post options / “Only you”.' });
      // ConnectionsSection lists accounts under ['social','accounts',projectKey];
      // the post-page form uses ['social-accounts', projectKey]. Refresh both.
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts', projectKey] });
      queryClient.invalidateQueries({ queryKey: ['social-accounts', projectKey] });
      onSaved?.(res);
    },
    onError: (err) => setMsg({ kind: 'err', text: err.body?.detail || err.message }),
  });

  return (
    <TikTokConsentFields
      entityKey={account.id}
      saved={saved}
      canEdit={account.status === 'active'}
      creator={creatorQ.data?.creator || null}
      creatorLoading={creatorQ.isLoading}
      creatorError={creatorQ.isError ? (creatorQ.error.body?.detail || creatorQ.error.message) : null}
      onRetryCreator={() => creatorQ.refetch()}
      accountHandle={account.external_account_handle}
      onSave={(options) => saveM.mutate(options)}
      onClear={() => saveM.mutate(null)}
      saving={saveM.isPending}
      msg={msg}
      onDirty={() => setMsg(null)}
    />
  );
}

/** Small modal wrapper shared by both buttons (portaled out of any <Link>). */
function OptionsModal({ title, subtitle, onClose, children }) {
  return createPortal(
    <div className="modal-backdrop" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}>
      {/* Content div must ONLY stopPropagation — preventDefault would cancel the
          activation of every checkbox/select/link inside. */}
      <div className="modal tiktok-opt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="section-h-row">
          <h2>{title}</h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">×</button>
        </div>
        {subtitle && <p className="muted small">{subtitle}</p>}
        {children}
      </div>
    </div>,
    document.body
  );
}

/**
 * Per-post trigger for the posts list and inbox approval cards. Renders a gear
 * icon only when this post's project has an active TikTok account it targets
 * (the /posts list supplies `tiktok_account_id`) and the post isn't a video.
 */
export function TikTokOptionsButton({ post, account, publications, className }) {
  const [open, setOpen] = useState(false);

  // List rows supply post.tiktok_account_id (the SQL gate); the post-detail
  // publish-targets row passes the tiktok `account` directly. Either one
  // identifies the account, and the post-detail post has no tiktok_account_id.
  const tiktokAccountId = account?.id || post.tiktok_account_id;
  if (!tiktokAccountId || isReelPost(post)) return null;
  // Respect per-post target overrides: when the post restricts targets and
  // this TikTok account is excluded, the form inside would render null —
  // a gear that opens an EMPTY modal (review 2026-08-24).
  const targetOverrides = post.post_target_overrides;
  if (Array.isArray(targetOverrides) && !targetOverrides.includes(tiktokAccountId)) return null;

  const hasOptions = Boolean(post.platform_options?.tiktok);
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const formPost = post.tiktok_account_id === tiktokAccountId
    ? post
    : { ...post, tiktok_account_id: tiktokAccountId };

  return (
    <>
      <button
        type="button"
        className={`btn btn-ghost btn-icon tt-opt-btn ${hasOptions ? 'tt-opt-set' : ''} ${className || ''}`}
        title={hasOptions ? 'TikTok options set — click to edit' : 'Set TikTok posting options'}
        aria-label="TikTok posting options"
        onClick={(e) => { stop(e); setOpen(true); }}
      >
        ⚙
      </button>
      {open && (
        <OptionsModal
          title="TikTok posting options"
          subtitle="Choose who can see this post on TikTok and confirm the usage terms. These apply only to the TikTok publication and override the connection default."
          onClose={() => setOpen(false)}
        >
          <TikTokOptionsForm post={formPost} publications={publications} />
        </OptionsModal>
      )}
    </>
  );
}

/**
 * Per-connection trigger for the Connections section: sets the DEFAULT TikTok
 * options for a connection (applied to every TikTok post that doesn't set its
 * own). Only rendered for an active TikTok account.
 */
export function TikTokDefaultsButton({ account, projectKey }) {
  const [open, setOpen] = useState(false);
  if (!account || account.platform !== 'tiktok') return null;
  // While the modal is OPEN, a background refetch flipping the account to
  // expired/refresh_failed must not unmount it mid-edit and eat the owner's
  // input — the form's own canEdit disables the fields instead.
  if (account.status !== 'active' && !open) return null;

  const hasDefaults = Boolean(account.default_post_options?.tiktok);

  return (
    <>
      <button
        type="button"
        className={`btn btn-ghost btn-icon tt-opt-btn ${hasDefaults ? 'tt-opt-set' : ''}`}
        title={hasDefaults ? 'Default TikTok options set — click to edit' : 'Set default TikTok posting options'}
        aria-label="Default TikTok posting options"
        onClick={() => setOpen(true)}
      >
        ⚙
      </button>
      {open && (
        <OptionsModal
          title="Default TikTok options"
          subtitle="Set these once and every TikTok post for this connection uses them by default — you can still override any individual post."
          onClose={() => setOpen(false)}
        >
          <TikTokDefaultsForm account={account} projectKey={projectKey} />
        </OptionsModal>
      )}
    </>
  );
}
